<app-dashboard-header></app-dashboard-header>

<div class="constraints-content">
  <h1 class="page-title">Constraints for {{ schedule?.name || 'Schedule' }}</h1>
  
  <mat-card>
    <div class="card-header">
      <h2>Manage Constraints</h2>
      <div class="header-actions">
        <button mat-stroked-button class="back-button" (click)="goBack()">
          <mat-icon>arrow_back</mat-icon>
          Back
        </button>
        <button mat-flat-button class="action-button accent" (click)="openAddDialog()">
          <mat-icon>add</mat-icon>
          Add Constraint
        </button>
      </div>
    </div>

    <!-- Loading Overlay -->
    <div *ngIf="loading" class="loading-overlay">
      <div class="loading-container">
        <mat-progress-spinner mode="indeterminate" diameter="60" color="primary"></mat-progress-spinner>
        <div class="loading-text">
          <h3>{{ loadingMessage }}</h3>
          <p>This may take a few moments...</p>
        </div>
      </div>
    </div>

    <div *ngIf="error" class="error-message">
      {{ error }}
    </div>

    <div *ngIf="!loading && constraints.length > 0" class="constraints-container">
      <!-- Hard Constraints Section -->
      <div class="constraint-section" *ngIf="hardConstraints.length > 0">
        <h3 class="section-title">
          <mat-icon class="section-icon hard-icon">lock</mat-icon>
          Hard Constraints (Must be satisfied)
        </h3>
        <div class="table-container">
          <table mat-table [dataSource]="hardConstraints" class="constraints-table" style="width: 100%;">

            <!-- Spacer Column (to align with drag column in soft constraints) -->
            <ng-container matColumnDef="spacer">
              <th mat-header-cell *matHeaderCellDef class="spacer-header-cell"></th>
              <td mat-cell *matCellDef="let constraint" class="spacer-cell"></td>
            </ng-container>

            <!-- ID Column -->
            <ng-container matColumnDef="id">
              <th mat-header-cell *matHeaderCellDef class="id-header-cell">ID</th>
              <td mat-cell *matCellDef="let constraint" class="id-cell">{{ constraint.id }}</td>
            </ng-container>

            <!-- Active Column -->
            <ng-container matColumnDef="active">
              <th mat-header-cell *matHeaderCellDef class="active-header-cell">Active</th>
              <td mat-cell *matCellDef="let constraint" class="active-cell">
                <div style="display: flex; align-items: center; gap: 8px;">
                  <mat-slide-toggle
                    [checked]="constraint.active"
                    [disabled]="constraint.togglingActive"
                    (change)="toggleConstraintActive(constraint)"
                    color="primary">
                  </mat-slide-toggle>
                  <mat-spinner *ngIf="constraint.togglingActive" diameter="20"></mat-spinner>
                </div>
              </td>
            </ng-container>

            <!-- Type Column -->
            <ng-container matColumnDef="type">
              <th mat-header-cell *matHeaderCellDef class="type-header-cell">Type</th>
              <td mat-cell *matCellDef="let constraint" class="type-cell">
                <span class="constraint-type hard-type">HARD</span>
              </td>
            </ng-container>

            <!-- Weight Spacer Column (to align with weight column in soft constraints) -->
            <ng-container matColumnDef="weightSpacer">
              <th mat-header-cell *matHeaderCellDef class="weight-header-cell">Weight</th>
              <td mat-cell *matCellDef="let constraint" class="weight-cell">
                <span class="weight-disabled">-</span>
              </td>
            </ng-container>

            <!-- Human Readable Definition Column -->
            <ng-container matColumnDef="humanReadableDefinition">
              <th mat-header-cell *matHeaderCellDef class="human-header-cell">Name</th>
              <td mat-cell *matCellDef="let constraint" class="human-cell">
                <div class="human-text human-text-padded">
                  {{ constraint.humanReadableDefinition }}
                  <mat-icon
                    *ngIf="constraint.hasPendingChanges"
                    class="pending-indicator"
                    [matTooltip]="constraint.addedInVersion ? 'Pending changes (not yet generated). Added in v' + constraint.addedInVersion : 'New constraint (not yet generated)'"
                  >pending_actions</mat-icon>
                  <mat-icon
                    *ngIf="!constraint.hasPendingChanges && constraint.addedInVersion"
                    class="version-indicator"
                    [matTooltip]="'Added in version ' + constraint.addedInVersion"
                  >history</mat-icon>
                </div>
                <div class="tech-preview full-width" [class.expanded]="constraint.expanded">
                  <span *ngIf="!constraint.expanded">
                    {{ constraint.definition | slice:0:120 }}<span *ngIf="constraint.definition.length > 120">…</span>
                    <a *ngIf="constraint.definition.length > 120" class="expand-link" (click)="constraint.expanded = true; $event.stopPropagation()">Show more</a>
                  </span>
                  <span *ngIf="constraint.expanded">
                    <pre class="tech-full">{{ constraint.definition }}</pre>
                    <a class="expand-link" (click)="constraint.expanded = false; $event.stopPropagation()">Show less</a>
                  </span>
                </div>
              </td>
            </ng-container>

            <!-- Actions Column -->
            <ng-container matColumnDef="actions">
              <th mat-header-cell *matHeaderCellDef style="width: 160px;">Actions</th>
              <td mat-cell *matCellDef="let constraint">
                <button mat-icon-button color="primary" matTooltip="Edit" (click)="openEditDialog(constraint)">
                  <mat-icon>edit</mat-icon>
                </button>
                <button mat-icon-button color="accent" matTooltip="Regenerate" (click)="regenerateConstraint(constraint)">
                  <mat-icon>refresh</mat-icon>
                </button>
                <button mat-icon-button color="warn" matTooltip="Delete" (click)="deleteConstraint(constraint)">
                  <mat-icon>delete</mat-icon>
                </button>
              </td>
            </ng-container>

            <tr mat-header-row *matHeaderRowDef="displayedColumnsHard" class="table-header-row"></tr>
            <tr mat-row *matRowDef="let row; columns: displayedColumnsHard;"></tr>
          </table>
        </div>
      </div>

      <!-- Soft Constraints Section -->
      <div class="constraint-section" *ngIf="softConstraints.length > 0">
        <h3 class="section-title">
          <mat-icon class="section-icon soft-icon">tune</mat-icon>
          Soft Constraints (Preferences - ordered by weight)
        </h3>
        <div class="table-container">
          <table mat-table [dataSource]="softConstraints" class="constraints-table" style="width: 100%;">

            <!-- Spacer Column (to align with hard constraints) -->
            <ng-container matColumnDef="spacer">
              <th mat-header-cell *matHeaderCellDef class="spacer-header-cell"></th>
              <td mat-cell *matCellDef="let constraint" class="spacer-cell"></td>
            </ng-container>

            <!-- ID Column -->
            <ng-container matColumnDef="id">
              <th mat-header-cell *matHeaderCellDef class="id-header-cell">ID</th>
              <td mat-cell *matCellDef="let constraint" class="id-cell">{{ constraint.id }}</td>
            </ng-container>

            <!-- Active Column -->
            <ng-container matColumnDef="active">
              <th mat-header-cell *matHeaderCellDef class="active-header-cell">Active</th>
              <td mat-cell *matCellDef="let constraint" class="active-cell">
                <div style="display: flex; align-items: center; gap: 8px;">
                  <mat-slide-toggle
                    [checked]="constraint.active"
                    [disabled]="constraint.togglingActive"
                    (change)="toggleConstraintActive(constraint)"
                    color="primary">
                  </mat-slide-toggle>
                  <mat-spinner *ngIf="constraint.togglingActive" diameter="20"></mat-spinner>
                </div>
              </td>
            </ng-container>

            <!-- Type Column -->
            <ng-container matColumnDef="type">
              <th mat-header-cell *matHeaderCellDef class="type-header-cell">Type</th>
              <td mat-cell *matCellDef="let constraint" class="type-cell">
                <span class="constraint-type soft-type">SOFT</span>
              </td>
            </ng-container>

            <!-- Weight Column (Display only) -->
            <ng-container matColumnDef="weight">
              <th mat-header-cell *matHeaderCellDef class="weight-header-cell">Weight</th>
              <td mat-cell *matCellDef="let constraint" class="weight-cell">
                <span class="weight-value-display">{{ constraint.weight || 100 }}</span>
              </td>
            </ng-container>

            <!-- Human Readable Definition Column -->
            <ng-container matColumnDef="humanReadableDefinition">
              <th mat-header-cell *matHeaderCellDef class="human-header-cell">Name</th>
              <td mat-cell *matCellDef="let constraint" class="human-cell">
                <div class="human-text human-text-padded">
                  {{ constraint.humanReadableDefinition }}
                  <mat-icon
                    *ngIf="constraint.hasPendingChanges"
                    class="pending-indicator"
                    [matTooltip]="constraint.addedInVersion ? 'Pending changes (not yet generated). Added in v' + constraint.addedInVersion : 'New constraint (not yet generated)'"
                  >pending_actions</mat-icon>
                  <mat-icon
                    *ngIf="!constraint.hasPendingChanges && constraint.addedInVersion"
                    class="version-indicator"
                    [matTooltip]="'Added in version ' + constraint.addedInVersion"
                  >history</mat-icon>
                </div>
                <div class="tech-preview full-width" [class.expanded]="constraint.expanded">
                  <span *ngIf="!constraint.expanded">
                    {{ constraint.definition | slice:0:120 }}<span *ngIf="constraint.definition.length > 120">…</span>
                    <a *ngIf="constraint.definition.length > 120" class="expand-link" (click)="constraint.expanded = true; $event.stopPropagation()">Show more</a>
                  </span>
                  <span *ngIf="constraint.expanded">
                    <pre class="tech-full">{{ constraint.definition }}</pre>
                    <a class="expand-link" (click)="constraint.expanded = false; $event.stopPropagation()">Show less</a>
                  </span>
                </div>
              </td>
            </ng-container>

            <!-- Actions Column -->
            <ng-container matColumnDef="actions">
              <th mat-header-cell *matHeaderCellDef style="width: 160px;">Actions</th>
              <td mat-cell *matCellDef="let constraint">
                <button mat-icon-button color="primary" matTooltip="Edit" (click)="openEditDialog(constraint)">
                  <mat-icon>edit</mat-icon>
                </button>
                <button mat-icon-button color="accent" matTooltip="Regenerate" (click)="regenerateConstraint(constraint)">
                  <mat-icon>refresh</mat-icon>
                </button>
                <button mat-icon-button color="warn" matTooltip="Delete" (click)="deleteConstraint(constraint)">
                  <mat-icon>delete</mat-icon>
                </button>
              </td>
            </ng-container>

            <tr mat-header-row *matHeaderRowDef="displayedColumns" class="table-header-row"></tr>
            <tr mat-row *matRowDef="let row; columns: displayedColumns;"></tr>
          </table>
        </div>
      </div>
    </div>

    <div *ngIf="!loading && constraints.length === 0" class="no-constraints">
      <mat-icon>info</mat-icon>
      <p>No constraints found. Click the "Add Constraint" button to create your first constraint.</p>
    </div>
  </mat-card>
</div>

<app-dashboard-footer></app-dashboard-footer>